import { RefObject, useEffect, useRef, useState } from 'react';
import { generatePath, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import queryString from 'query-string';
import classNames from 'classnames';
import { useDebounceValue, useOnClickOutside } from 'usehooks-ts'
import { t } from 'ttag';

import SearchIcon from '@svg/search.svg';
import CloseIcon from '@svg/close.svg';
import { tag } from '@legacyUrls';
import { publicMemberProfile } from '@urls';
import { FILTER_QUERIES } from '@constants';
import { sendSearchApplyEvent, sendSearchOpenEvent } from '@utils/analytics';
import { matchCatalogPath, matchCollectionPath } from '@utils/common';
import { Button } from '@ui/Button';
import { Input } from '@ui/Input';
import { SelectOption } from '@ui/Select';
import { HeaderCatalogSelect } from '@components/HeaderCatalogSelect';
import { SearchSuggestsContainer, type OnSaggestsPayload, type Suggest, type Category } from '@containers/SearchSuggests';

import { getSuggestsAnalyticsData } from './utils';

import s from './HeaderSearch.sss';

const MIN_SEARCH_LENGTH_FOR_SUGGESTS = 2;

type CatalogOption = SelectOption<string>;

interface Props {
  selectedCatalog: CatalogOption,
  catalogsOptions: CatalogOption[]
  className?: string,
  isMobile?: boolean,
  onSelectCatalog: (option: CatalogOption) => void,
}

export const HeaderSearch: React.FC<Props> = ({
  selectedCatalog,
  catalogsOptions,
  className,
  isMobile,
  onSelectCatalog,
}) => {
  const location = useLocation();
  const history = useHistory();

  const currentSuggestsRef = useRef<OnSaggestsPayload>({
    suggests: [],
    categoriesSuggests: [],
    suggestsInCategories: [],
  });

  const formRef = useRef<HTMLFormElement>(null);

  const [isSearchInputFocused, setIsSearchInputFocused] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const [debouncedSearchValue, setDebouncedSearchValue] = useDebounceValue(searchValue, 500)

  const publicMemberProfileMatch = useRouteMatch<{ username: string }>(publicMemberProfile);
  const selectedCatalogMatch = matchCatalogPath(selectedCatalog.value);
  const selectedCollectionMatch = matchCollectionPath(selectedCatalog.value);

  const selectedNamespace = selectedCatalogMatch?.params.namespace;
  const selectedCatalogSlug = selectedCatalogMatch && 'slug' in selectedCatalogMatch?.params
    ? selectedCatalogMatch?.params.slug
    : undefined;
  const selectedCollectionSlug = selectedCollectionMatch?.params.slug;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setDebouncedSearchValue(e.target.value);
  };

  const handleFocusInput = () => {
    setIsSearchInputFocused(true);
    sendSearchOpenEvent();
  };

  const handleHideSuggests = () => {
    setIsSearchInputFocused(false);
  };

  const handleSuggestClick = (suggest: Suggest) => {
    const {
      suggestsBlocks,
      suggestsCount,
      searchQuery,
    } = getSuggestsAnalyticsData({
      suggests: currentSuggestsRef.current.suggests,
      categoriesSuggests: currentSuggestsRef.current.categoriesSuggests,
      suggestsInCategories: currentSuggestsRef.current.suggestsInCategories,
    });

    sendSearchApplyEvent({
      namespace: selectedNamespace,
      categorySlug: selectedCatalogSlug,
      collection: selectedCollectionSlug,
      referrer: suggest.replacementOfSuggest ? 'suggest_tag' : 'suggest_common',
      suggests: suggestsBlocks,
      suggestsCount,
      searchQuery,
      searchQueryClick: suggest.search,
    });

    if (suggest.replacementOfSuggest) {
      window.location.href = decodeURIComponent(
        generatePath(tag, { slug: suggest.replacementOfSuggest.slug }),
      );
    } else {
      if (window.IS_SPA_PAGE) {
        history.push({
          pathname:  selectedCatalog.value,
          search: queryString.stringify({ search_text: suggest.search }),
        });
      } else {
        window.location.href = history.createHref({
          pathname: selectedCatalog.value,
          search: queryString.stringify({ search_text: suggest.search }),
        });
      }
    }

    handleHideSuggests();
  };

  const handleCategorySuggestClick = (category: Category) => {
     const {
      suggestsBlocks,
      suggestsCount,
      searchQuery,
    } = getSuggestsAnalyticsData({
      suggests: currentSuggestsRef.current.suggests,
      categoriesSuggests: currentSuggestsRef.current.categoriesSuggests,
      suggestsInCategories: currentSuggestsRef.current.suggestsInCategories,
    });

    sendSearchApplyEvent({
      namespace: selectedNamespace,
      categorySlug: selectedCatalogSlug,
      collection: selectedCollectionSlug,
      referrer: 'suggest_choose_category',
      suggests: suggestsBlocks,
      suggestsCount,
      searchQuery,
      searchQueryClick: category.name,
    });

    handleHideSuggests();
  };

  const handleSuggestInCategoryClick = (category: Category) => {
    const {
      suggestsBlocks,
      suggestsCount,
      searchQuery,
    } = getSuggestsAnalyticsData({
      suggests: currentSuggestsRef.current.suggests,
      categoriesSuggests: currentSuggestsRef.current.categoriesSuggests,
      suggestsInCategories: currentSuggestsRef.current.suggestsInCategories,
    });

    sendSearchApplyEvent({
      namespace: selectedNamespace,
      categorySlug: selectedCatalogSlug,
      collection: selectedCollectionSlug,
      referrer: 'suggest_in_category',
      suggests: suggestsBlocks,
      suggestsCount,
      searchQuery,
      searchQueryClick: category.name,
    });

    handleHideSuggests();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    handleHideSuggests();

    const {
      suggestsBlocks,
      suggestsCount,
      searchQuery,
    } = getSuggestsAnalyticsData({
      suggests: currentSuggestsRef.current.suggests,
      categoriesSuggests: currentSuggestsRef.current.categoriesSuggests,
      suggestsInCategories: currentSuggestsRef.current.suggestsInCategories,
    });

    sendSearchApplyEvent({
      namespace: selectedNamespace,
      categorySlug: selectedCatalogSlug,
      collection: selectedCollectionSlug,
      referrer: 'regular',
      suggests: suggestsBlocks,
      suggestsCount,
      searchQuery,
      searchQueryClick: searchValue,
    });

    const pathname = selectedCatalog.value;
    const search = searchValue ? queryString.stringify({ search_text: searchValue }) : undefined

    if (window.IS_SPA_PAGE) {
      history.push({
        pathname,
        search,
      });
    } else {
      window.location.href = history.createHref({
        pathname: pathname,
        search,
      });
    }
  };

  useOnClickOutside<HTMLFormElement>(formRef as RefObject<HTMLFormElement>, handleHideSuggests);

  useEffect(() => {
    const search = queryString.parse(location.search);
    setSearchValue(search[FILTER_QUERIES.SEARCH_TEXT] as string || '');
  }, [location.search]);

  const submitUrl = history.createHref({
    pathname: selectedCatalog.value.startsWith('/')
      ? selectedCatalog.value
      : `/${selectedCatalog.value}`,
  });

  return (
    <div
      className={classNames(s.root, className)}>
      <HeaderCatalogSelect
        className={s.catalogSelect}
        options={catalogsOptions}
        value={selectedCatalog}
        onChange={onSelectCatalog}
      />
      <form
        ref={formRef}
        className={s.form}
        action={submitUrl}
        method="GET"
        onSubmit={handleSubmit}
      >
        <Input
          className={s.inputHolder}
          inputClassName={s.input}
          theme="transparent"
          placeholder={t`Я хочу купити на SHAFA ...`}
          name={FILTER_QUERIES.SEARCH_TEXT}
          autoFocus={isMobile}
          value={searchValue}
          onChange={handleInputChange}
          onFocus={handleFocusInput}
          autoComplete='off'
        />
        {!!searchValue && (
          <Button
            className={s.button}
            theme='text'
            onClick={() => setSearchValue('')}
          >
            <CloseIcon
              className={s.icon}
              width="24"
              height="24"
            />
          </Button>
        )}

        <Button
          className={s.button}
          theme='text'
          type="submit"
        >
          <SearchIcon
            className={s.icon}
            width="24"
            height="24"
          />
        </Button>

        {(isSearchInputFocused
          && debouncedSearchValue.length > MIN_SEARCH_LENGTH_FOR_SUGGESTS
          && !publicMemberProfileMatch
        ) && (
          <SearchSuggestsContainer
            className={s.suggests}
            searchText={debouncedSearchValue}
            catalogSlug={selectedCatalogSlug || selectedNamespace}
            collectionSlug={selectedCollectionSlug}
            onSuggests={(suggests) => currentSuggestsRef.current = suggests}
            onSuggestClick={handleSuggestClick}
            onCategorySuggestClick={handleCategorySuggestClick}
            onSuggestInCategoryClick={handleSuggestInCategoryClick}
          />
        )}
      </form>
    </div>
  );
};
